import { captureException } from "@sentry/vue";

export class PollingManager {
  constructor(apiUrl, itemId, updateCallback, messageHandler, batchCompleteCallback) {
    this.apiUrl = apiUrl;
    this.itemId = itemId;
    this.updateCallback = updateCallback;
    this.messageHandler = messageHandler;
    this.batchCompleteCallback = batchCompleteCallback;
    this.processedItems = new Set();
    this.messageQueue = [];
    this.isProcessingQueue = false;
    this.pollInterval = null;
    this.processQueueInterval = null;
    this.pollingStopped = false;
    this.currentRequestId = 0;
    this.boundMessageListener = null;
    this.attempts = 0;
  }

  async startPolling() {
    console.log("Started polling every 3s");
    await this.poll();
    this.pollInterval = setInterval(async () => {
      await this.poll();
    }, 3000);
    await this.startProcessingQueue();
  }

  async startProcessingQueue() {
    console.log("Started processing queue every 1s");
    await this.processQueue();
    if (!this.pollingStopped || this.messageQueue.length > 0) {
      this.processQueueInterval = setInterval(async () => {
        await this.processQueue();
      }, 1000);
    }
  }

  async poll() {
    this.attempts++;
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        body: JSON.stringify({"item_id": this.itemId})
      });
      const data = await response.json();
      const items = JSON.parse(data.body).slides;
      if (items.length > 0) {
        this.processItems(items);
        if (items[items.length - 1].done) {
          this.stopPolling();
          return;
        }
      } else {
        this.stopPolling();
        return;
      }
    } catch (error) {
      console.error('Polling error:', error);
      captureException(error, { level: "error" });
    } finally {
      if (this.attempts > 100) {
        this.stopPolling();
        return;
      }
    }
  }

  processItems(items) {
    const hasDoneIndicator = items.length > 0 && items[items.length - 1]?.slide_number === '';
    const processableItems = hasDoneIndicator ? items.slice(1, -1) : items.slice(1);
    const batch = [];

    processableItems.forEach((item) => {
      if (item.status === 'succeeded' && !this.processedItems.has(item.slide_number)) {
        console.log(`Adding slide number ${item.slide_number} to batch`);
        this.processedItems.add(item.slide_number);
        batch.push(item);
      }
    });

    if (batch.length > 0) {
      console.log(`Pushing batch of ${batch.length} items to queue`);
      this.messageQueue.push(batch);
    }
  }

  async processQueue() {
    if (this.isProcessingQueue || this.messageQueue.length === 0) {
      if (this.isProcessingQueue) {
        console.log(`Can't process queue because it's already processing`);
      } else if (this.messageQueue.length === 0) {
        console.log(`Can't process queue because it's empty`);
      }
      return;
    }

    this.isProcessingQueue = true;
    const batch = this.messageQueue[0];
    const slideNumbers = batch.map(item => item.slide_number);
    console.log(`Processing batch of ${batch.length} items: slide numbers ${slideNumbers}`);
    const requestId = this.currentRequestId++;

    try {
      let result = await this.messageHandler(batch, requestId);
      let recoveryAttempts = 0;
      while (!result && recoveryAttempts < 10) {
        recoveryAttempts += 1;
        result = await this.messageHandler(batch, requestId, true);
      }
      console.log(result);
      batch.forEach((item, index) => {
        this.updateCallback(index, item, result ? result.last_inserted_name : null);
      });
      this.batchCompleteCallback?.();
    } catch (error) {
      console.error('Batch processing error:', error);
      captureException(error, { level: "error" });
    } finally {
      this.messageQueue.shift();
      this.isProcessingQueue = false;
      console.log("Checking whether we should stop processing queue");
      if (this.pollingStopped && this.messageQueue.length === 0) {
        this.stopProcessingQueue();
      }
    }
  }

  stopPolling() {
    clearInterval(this.pollInterval);
    console.log("Stopped polling");
    this.pollingStopped = true;
    if (!this.isProcessingQueue && this.messageQueue.length === 0) {
      this.stopProcessingQueue();
    }
  }

  stopProcessingQueue() {
    clearInterval(this.processQueueInterval);
    console.log("Stopped processing queue");
  }
}
